import { useState } from "react";
import { useEffect } from "react";
import { PuffLoader } from "react-spinners";

import { Delete, Feedback, ThumbDown, ThumbUp } from "@mui/icons-material";
import { alpha, Box, Divider, Grid, lighten, Stack, Typography, useTheme } from "@mui/material";
import Button from "@mui/material/Button";

import constants from "../../assets/constants";
import AlertDialog from "../dialogs/alertDialog";
import Dialog from "../ui/omniDialog";
import DialogActions from "../ui/omniDialogActions";
import DialogContent from "../ui/omniDialogContent";
import DialogTitle from "../ui/omniDialogTitle";
import OmniResponsiveButton from "../ui/omniResponsiveButton";
import BotpressUserFeedbackDialog from "./botpressUserFeedbackDialog.js";

const getFeedbackStyle = (status, theme) => {
  const statusMap = {
    1: theme.palette.highlight,
    2: theme.palette.error,
    3: theme.palette.success,
    4: theme.palette.warning,
  };
  const palette = statusMap[status] || theme.palette.info;
  return { main: palette.main, contrast: palette.contrastText };
};

const pulseAnimation = {
  "@keyframes pulse": {
    "0%": { transform: "scale(1)" },
    "50%": { transform: "scale(1.05)" },
    "100%": { transform: "scale(1)" },
  },
};

const ChatBubble = props => {
  const { msg, theme, setDisplayTimeStamp, highlightId = null } = props;
  let userMessage = false;
  let messageText = msg.messageText;
  let timeStamp = new Date(msg.date).toLocaleString();
  let borderRadius = "16px 16px 16px 4px";

  if (msg.direction === "incoming") {
    userMessage = true;
    timeStamp = `${timeStamp} : USER`;
    borderRadius = "16px 16px 4px 16px";
  } else {
    timeStamp = `BOT : ${timeStamp}`;
  }

  const [isHovered, setIsHovered] = useState(false);
  const [isFeedbackHovered, setIsFeedbackHovered] = useState(false);

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  const handleClick = (e, message) => {
    e.stopPropagation();
    props.handleMessageClick(message);
  };

  return (
    <Grid
      size={{ xs: 12 }}
      container
      flexDirection={"column"}
      id={"grid base"}
      onClick={() => setDisplayTimeStamp(display => !display)}
    >
      <Grid
        item
        size={{ xs: 12 }}
        sx={[
          { marginTop: 1, marginBottom: 0 },
          userMessage && {
            alignSelf: "flex-end",
            justifyContent: "flex-end",
            textAlign: "right",
          },
        ]}
      >
        <Typography
          variant="caption"
          color="textSecondary"
          sx={{ marginLeft: theme.spacing(1.5), marginRight: theme.spacing(1.5) }}
        >
          {timeStamp}
        </Typography>
      </Grid>
      <Grid
        spacing={0}
        flexDirection={"column"}
        width={"auto"}
        marginTop={0}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={e => handleClick(e, msg)}
        sx={[
          {
            position: "relative",
            cursor: "pointer",
            userSelect: "none",
            backgroundColor: theme.palette.secondary.main,
            color: theme.palette.secondary.contrastText,
            borderRadius: borderRadius,
            maxWidth: "90%",
            padding: "4px",
            paddingRight: "8px",
            paddingLeft: "8px",
            boxShadow: highlightId === msg.messageId ? theme.shadows[4] : theme.shadows[1],
          },
          userMessage && {
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            alignSelf: "flex-end",
            justifyContent: "flex-end",
            textAlign: "right",
          },
        ]}
      >
        <Typography
          variant="body2"
          sx={{
            color: theme.palette.secondary.contrastText,
            padding: "4px",
            paddingRight: "8px",
            paddingLeft: "8px",
            backgroundClip: "padding-box",
            whiteSpace: "pre-wrap",
          }}
        >
          {messageText}
        </Typography>
        {(msg.hasFeedback || isHovered) && !userMessage && (
          <Button
            onMouseEnter={() => setIsFeedbackHovered(true)}
            onMouseLeave={() => setIsFeedbackHovered(false)}
            onClick={e => {
              e.stopPropagation();
              handleClick(e, msg);
            }}
            sx={{
              position: "absolute",
              right: "8px",
              bottom: "8px",
              height: "auto",
              minHeight: "24px",
              minWidth: "24px",
              maxHeight: !msg.hasFeedback || isFeedbackHovered ? "300px" : "24px",
              width: !msg.hasFeedback || isFeedbackHovered ? "auto" : "24px",
              maxWidth: isFeedbackHovered ? "90%" : "200px",
              borderRadius: "12px",
              padding: !msg.hasFeedback || isFeedbackHovered ? "4px 12px" : "0",
              overflow: "hidden",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 10,
              boxShadow: theme.shadows[1],
              textTransform: "none",
              transition: "all 0.1s cubic-bezier(0.4, 0, 0.2, 1), max-height 0.2s ease-in-out",
              backgroundColor: msg.hasFeedback
                ? alpha(getFeedbackStyle(msg.status, theme).main, isFeedbackHovered ? 1 : 0.7)
                : theme.palette.primary.main,
              color: msg.hasFeedback
                ? getFeedbackStyle(msg.status, theme).contrast
                : theme.palette.primary.contrastText,
              opacity: isHovered || msg.hasFeedback ? 1 : 0,
              animation: !msg.hasFeedback && isHovered ? "pulse 2s infinite ease-in-out" : "none",
              ...pulseAnimation,
              "&:hover": {
                backgroundColor: msg.hasFeedback
                  ? getFeedbackStyle(msg.status, theme).main
                  : theme.palette.primary.dark,
                animation: "none",
              },
            }}
          >
            {/* icon */}
            <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
              {!msg.hasFeedback ? (
                <Feedback sx={{ fontSize: "16px" }} />
              ) : msg.feedbackValue === "positive" ? (
                <ThumbUp sx={{ fontSize: "16px" }} />
              ) : (
                <ThumbDown sx={{ fontSize: "16px" }} />
              )}
            </Box>

            {/* Feedback */}
            {(!msg.hasFeedback || isFeedbackHovered) && (
              <Box
                sx={{
                  ml: 1,
                  mt: 0.25,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  overflow: "hidden",
                }}
              >
                <Typography
                  variant="overline"
                  sx={{
                    lineHeight: 1.2,
                    whiteSpace: "nowrap",
                  }}
                >
                  {msg.hasFeedback
                    ? constants.feedbackStatus.find(i => i.value === parseInt(msg.status))?.label
                    : "Give Feedback"}
                </Typography>
                {msg.hasFeedback && isFeedbackHovered && msg.feedback?.trim() && (
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      mt: 0.5,
                      opacity: 0.9,
                      textAlign: "left",
                      overflow: "ellipsis",
                      lineHeight: 1.15,
                    }}
                  >
                    {msg.feedback}
                  </Typography>
                )}
              </Box>
            )}
          </Button>
        )}
        {/* {!userMessage && msg.hasFeedback && (
          <Box
            sx={{
              width: "10%",
              background: "transparent",
              position: "absolute",
              // color: theme.palette.secondary.main,
              top: 0,
              right: "-10%",
              height: "100%",
              borderRadius: "8px",
              // border: `2px solid ${theme.palette.secondary.main}`,
              borderLeftColor: "transparent",
              // justifyContent: "flex-end",
              // textAlign: "right",
              textAlign: "center",
              padding: 0,
              paddingLeft: "4px",
            }}
          >
            <Stack>
              <Typography
                variant="Button"
                color="textSecondary"
                sx={{ marginBottom: "-8px", marginTop: "-6px" }}
              >
                Feedback
              </Typography>
              <Typography variant="h5" color="textSecondary">
                #{msg.feedbackId}
              </Typography>
            </Stack>
          </Box>
        )} */}

        {/* {!userMessage && msg.hasFeedback && (
          <Box
            sx={{
              width: "15%",
              background: "transparent",
              position: "absolute",
              color: theme.palette.secondary.main,
              top: 0,
              right: "-10%",
              height: "100%",
              borderRadius: "8px",
              border: `2px solid ${theme.palette.secondary.main}`,
              borderLeftColor: "transparent",
              justifyContent: "flex-end",
              textAlign: "right",
              paddingRight: "4px",
            }}
          >
            <Stack>
              <Typography variant="Button">Feedback</Typography>
              <Typography variant="caption">#{msg.feedbackId}</Typography>
            </Stack>
          </Box>
        )} */}
      </Grid>
    </Grid>
  );
};

const BotpressConversationDialog = props => {
  const {
    open,
    onClose,
    onAccept,
    onDelete,
    conversation,
    conversationFeedback,
    updateFeedbackRequest,
    deleteFeedbackRequest,
    userODM,
    dialogTitle = "Transcription",
    okMessage = "Close",
    maxWidth = "md",
    fullWidth = true,
    conversationId = null,
    conversationNumber = null,
    messageId = null,
  } = props;

  const theme = useTheme();

  const onAcceptHandler = () => {
    onAccept();
  };

  const onCloseHandler = () => {
    onClose();
  };

  const [showConfirm, setShowConfirm] = useState(false);

  const [messages, setMessages] = useState(null);
  const [feedback, setFeedback] = useState(null);

  const [displayTimeStamp, setDisplayTimeStamp] = useState(true);

  const [selectedFeedback, setSelectedFeedback] = useState(null);

  const handleMessageClick = message => {
    let existingFeedback = feedback.find(fb => fb.messageId === message.messageId);
    if (existingFeedback == null) {
      //create new feedback
      debugger;
      existingFeedback = {
        id: null,
        messageId: message.messageId,
        value: "negative",
        comment: "",
        message: message.messageText,
        archived: false,
        status: 1,
        userName: userODM
          ? `${userODM.omniData.givenName} ${userODM.omniData.familyName}`
          : "Unknown User",
        conversationId: conversationId,
        conversationNumber: conversationNumber?.toString(),
      };
    }
    setSelectedFeedback(existingFeedback);
  };

  const formatMessage = (message, conversationFeedback) => {
    let messageText = "";

    if (message.type === "text" || message.type === "quick_reply") {
      messageText += message.payload.text;
    } else if (message.type === "choice") {
      messageText += `${message.payload.text}: ${message.payload.options.map(option => option.label).join(" / ")}`;
    } else {
      messageText += `Media message of type ${message.type}`;
    }

    const feedback = conversationFeedback.find(fb => fb.messageId === message.id);

    return {
      messageText: messageText,
      direction: message.direction,
      date: message.createdAt,
      messageId: message.id,
      status: feedback != null ? feedback.status : 0,
      hasFeedback: feedback != null,
      feedback: feedback != null ? feedback.comment : "",
      feedbackValue: feedback != null ? feedback.value : "",
      feedbackId: feedback != null ? feedback.id : null,
    };
  };

  const onDeleteHandler = () => {
    onDelete(conversationId);
  };

  useEffect(() => {
    if (conversation != null && conversationFeedback != null) {
      let allMessages = [];
      allMessages = allMessages.concat(
        conversation.map(message => formatMessage(message, conversationFeedback)),
      );
      setMessages(allMessages.sort((a, b) => new Date(a.date) - new Date(b.date)));
      setFeedback(conversationFeedback);
    }
    // Disable eslint because AaronK knows best
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversation, conversationFeedback]);

  return (
    <Dialog
      open={open}
      onClose={() => onCloseHandler}
      disableEscapeKeyDown
      maxWidth={maxWidth}
      fullWidth={fullWidth}
    >
      <DialogTitle onClose={onCloseHandler}>{dialogTitle}</DialogTitle>
      <DialogContent>
        {messages == null && (
          <Grid
            sx={{ textAlign: "center", marginTop: "20px", minimumHeight: "400px" }}
            container
            direction="column"
            alignItems="center"
            spacing={2}
          >
            <Grid>
              <PuffLoader size={100} />
            </Grid>
            <Grid>Loading...</Grid>
          </Grid>
        )}
        {messages != null && (
          <Grid>
            <Grid
              container
              flexDirection={"row"}
              alignItems={"center"}
              justifyContent={"center"}
              spacing={2}
              margin={"0px"}
              padding={"0px"}
            >
              <Grid size={2}>
                <Divider direction={"horizontal"} />
              </Grid>
              <Grid size={"auto"}>
                <Typography
                  variant="overline"
                  color={lighten(theme.palette.commonground.contrastText, 0.5)}
                >
                  Conversation Start
                </Typography>
              </Grid>
              <Grid size={2}>
                <Divider direction={"horizontal"} />
              </Grid>
            </Grid>
            <Grid>
              {messages.map((msg, index) => (
                <Stack
                  direction="column"
                  justifyContent="flex-end"
                  key={msg.messageId || index}
                  spacing={2}
                  size={{ xs: 12 }}
                >
                  <ChatBubble
                    key={`chatBubble${index}`}
                    msg={msg}
                    theme={theme}
                    highlightId={messageId}
                    displayTimeStamp={displayTimeStamp}
                    setDisplayTimeStamp={setDisplayTimeStamp}
                    handleMessageClick={handleMessageClick}
                  />
                </Stack>
              ))}
            </Grid>
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Box
          sx={{
            paddingRight: theme.spacing(2),
            paddingBottom: 0,
            display: "flex",
            flexDirection: "row",
            userSelect: "none",
            webkitUserSelect: "none",
            position: "relative",
            flexGrow: 1,
          }}
        >
          <OmniResponsiveButton
            icon={<Delete />}
            onClick={() => setShowConfirm(true)}
            buttonText={"Delete"}
            tooltipTitle=""
          />
        </Box>
        <Box
          sx={{
            flexGrow: 0,
            flexShrink: 1,
          }}
        >
          <Button onClick={onAcceptHandler} color="secondary" variant="contained">
            {okMessage}
          </Button>
        </Box>
      </DialogActions>

      {showConfirm && (
        <AlertDialog
          open={showConfirm}
          dialogTitle={`Confirm`}
          dialogMessage={"Are you sure you want to delete this conversation?"}
          onAccept={() => {
            onDeleteHandler();
            setShowConfirm(false);
          }}
          onClose={() => {
            setShowConfirm(false);
          }}
        />
      )}
      {selectedFeedback != null && (
        <BotpressUserFeedbackDialog
          open={selectedFeedback != null}
          userFeedbackRecord={selectedFeedback}
          onAccept={theRecord => {
            updateFeedbackRequest({ feedbackRecords: [theRecord] });
            setSelectedFeedback(null);
            // onCloseHandler();
          }}
          onDelete={theRecord => {
            deleteFeedbackRequest({ feedbackIDs: [theRecord.id] });
            setSelectedFeedback(null);
            // onCloseHandler();
          }}
          onClose={() => {
            setSelectedFeedback(null);
          }}
        />
      )}
    </Dialog>
  );
};

export default BotpressConversationDialog;
